import { useEffect, useRef, useState } from "react";
import { describeError } from "../api/errors";
import { useApi } from "../api/context";
import { Avatar } from "../components/Avatar";
import { Icon } from "../components/Icon";
import { Link } from "../components/Link";
import { Spinner } from "../components/Spinner";
import { t } from "../i18n";
import { track } from "../lib/analytics";
import { isSubscribed } from "../lib/episodes";
import { takeCode } from "../lib/landing";
import { chargeLine, ctaLabel, planOffers, trialDaysFor, type PlanId } from "../lib/money";
import { back, go, href, navigate, routeHref, type Route } from "../lib/router";
import { useHeroData } from "../state/heroData";

type PlansRoute = Extract<Route, { name: "plans" }>;

/**
 * Пейвол. Годовой план выбран по умолчанию и показан ценой в месяц — так его сравнивают
 * с месячным честно. Пробный период и напоминание о списании видны до оплаты, а не
 * мелким шрифтом в Stripe. «Всё сразу» — отдельной строкой со своим объяснением: это
 * не подписка, и человек должен понимать, за что платит.
 */
export function Paywall({ route }: { route: PlansRoute }) {
  const api = useApi();
  const { hero, me, episodes } = useHeroData();
  const { pricing } = hero;
  const subscribed = isSubscribed(me);

  // Подписчику подписку второй раз не предлагаем, купившему сезон — сезон второй раз
  const trialEligible = me ? me.trialEligible : null;
  const trialDays = trialDaysFor(pricing, trialEligible);
  const offers = planOffers(pricing, trialEligible).filter(
    (offer) =>
      (!subscribed || offer.plan === "all_at_once") && !(offer.plan === "all_at_once" && me?.unlockedAll === true),
  );
  const everything = subscribed && offers.length === 0;
  const initialPlan: PlanId = offers.find((offer) => offer.plan === route.plan)?.plan ?? offers[0]?.plan ?? "yearly";
  const [plan, setPlan] = useState<PlanId>(initialPlan);
  const [busy, setBusy] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resumed = useRef(false);

  const selected = offers.find((offer) => offer.plan === plan) ?? offers[0] ?? null;
  const waiting = route.episode === null ? null : episodes.find((episode) => episode.ordinal === route.episode) ?? null;

  // Показ пейвола — один на заход. Возврат после регистрации (?resume=1) — не новый показ:
  // человек уже выбрал план, и второй «paywall_shown» раздул бы воронку. Признак берётся
  // в момент захода: ?resume=1 тут же снимается с адреса, и эффект увидел бы уже false
  const shown = useRef(false);
  const cameToResume = useRef(route.resume);
  useEffect(() => {
    if (shown.current || cameToResume.current) return;
    shown.current = true;
    track("paywall_shown", { episode: route.episode ?? undefined, screen: "plans" });
  }, [route.episode]);

  async function startCheckout(chosen: PlanId) {
    if (!me) {
      // Сначала аккаунт (имя для серий), потом сразу обратно к оплате выбранного плана
      navigate(href.join(href.plans({ episode: route.episode, plan: chosen, resume: true })));
      return;
    }
    setBusy(true);
    setError(null);
    track("checkout_started", { screen: "plans" });
    try {
      const code = takeCode();
      const { url } = await api.checkout({ plan: chosen, ...(code ? { code } : {}) });
      go(url);
    } catch (checkoutError) {
      setError(describeError(checkoutError, "checkout"));
      setBusy(false);
      setResuming(false);
    }
  }

  // Вернулись после регистрации с ?resume=1 — продолжаем оплату без второго нажатия
  useEffect(() => {
    if (!route.resume || resumed.current || !me || everything) return;
    resumed.current = true;
    setResuming(true);
    // ?resume=1 из адреса убираем: «назад» из Stripe не должен запускать оплату снова
    navigate(routeHref({ ...route, resume: false }), { replace: true });
    void startCheckout(initialPlan);
  }, [route.resume, me]);

  const chat = hero.format === "chat";
  const title = waiting
    ? t("paywall.titleEpisode", { n: waiting.ordinal })
    : t(chat ? "paywall.titleChat" : "paywall.title", { hero: hero.name });
  // Герой-переписка: «назад» — в переписку, а не к сериям
  const backTo = chat ? href.chat() : href.listen();
  const backLabel = chat ? t("paywall.backToChat") : t("paywall.backToListening");

  return (
    <main className="container narrow paywall">
      <button type="button" className="icon-btn icon-btn--quiet paywall__close" onClick={() => back(href.hero())} aria-label={t("app.close")}>
        <Icon name="close" />
      </button>

      <div className="paywall__head">
        <Avatar src={hero.theme.portraitUrl} name={hero.name} size={72} />
        {subscribed ? (
          <>
            <h1>{everything ? t("paywall.everything.title") : t("paywall.subscribed.title")}</h1>
            <p className="muted">{everything ? t("paywall.everything.body") : t("paywall.subscribed.body")}</p>
          </>
        ) : (
          <>
            <h1>{title}</h1>
            {waiting && <p className="paywall__episode">“{waiting.title}”</p>}
            <p className="muted">
              {trialDays > 0 ? t("paywall.subtitle", { days: trialDays }) : t("paywall.subtitleNoTrial")}
            </p>
          </>
        )}
      </div>

      {route.canceled && <p className="notice">{t("paywall.canceled")}</p>}

      {resuming ? (
        <div className="paywall__redirect">
          <Spinner label={t("paywall.redirecting")} />
          <p>{t("paywall.redirecting")}</p>
        </div>
      ) : everything ? (
        <Link to={backTo} className="btn btn--primary btn--lg btn--block">
          {backLabel}
        </Link>
      ) : (
        <>
          <fieldset className="plans">
            <legend className="sr-only">{t("paywall.plans")}</legend>
            {offers.map((offer) => (
              <label key={offer.plan} className={`plan plan--${offer.plan}${plan === offer.plan ? " plan--selected" : ""}`}>
                <input
                  type="radio"
                  name="plan"
                  value={offer.plan}
                  checked={plan === offer.plan}
                  onChange={() => setPlan(offer.plan)}
                  className="plan__radio"
                />
                <span className="plan__mark" aria-hidden="true" />
                <span className="plan__name">
                  {t(`paywall.plan.${offer.plan}`)}
                  {offer.badge && <span className="badge badge--accent">{offer.badge}</span>}
                </span>
                <span className="plan__price">
                  <span className="plan__headline">{offer.headline}</span>
                  <span className="plan__detail">{offer.detail}</span>
                </span>
                {offer.plan === "all_at_once" && (
                  <span className="plan__note">
                    {t(offer.demoDayPass ? "paywall.demoDay.note" : "paywall.allAtOnceNote")}
                  </span>
                )}
              </label>
            ))}
          </fieldset>

          {selected && (
            <div className="paywall__cta">
              <button
                type="button"
                className="btn btn--primary btn--lg btn--block"
                onClick={() => void startCheckout(selected.plan)}
                disabled={busy}
              >
                {busy ? t("paywall.redirecting") : ctaLabel(selected, pricing)}
              </button>
              <p className="paywall__charge">{chargeLine(selected, pricing)}</p>
              {selected.trial && (
                <p className="paywall__reminder">
                  <Icon name="bell" size={18} />
                  {t("paywall.reminder")}
                </p>
              )}
              {!me && <p className="hint center">{t("paywall.accountNext")}</p>}
              {error && (
                <p className="form__error" role="alert">
                  {error}
                </p>
              )}
              <p className="hint center">{t(selected.demoDayPass ? "paywall.demoDay.fine" : "paywall.fine")}</p>
            </div>
          )}

          {subscribed && (
            <Link to={backTo} className="btn btn--quiet btn--block">
              {backLabel}
            </Link>
          )}
        </>
      )}
    </main>
  );
}
